/**
 * @file PickingTaskHeaderActions.tsx
 * @description Действия задания на сборку для шапки документа (phase19.23;
 * конвенция «одна главная кнопка + ⋯» — phase19.24 этап 1, шаг 1;
 * phase19.31 зона «Склад» — хук для шапки в верхней панели).
 *
 * Главное действие меняется по состоянию: задание ничьё — «Взять в работу»;
 * есть недостача по строкам — «Оформить недостачу» (§3.3); иначе «Провести
 * сборку». Остальное (лист комплектации, возврат в очередь, пересборка,
 * отмена, звук скана) — пункты «⋯».
 *
 * Хук, а не кнопки на месте: шапку рисует `CabinetPageHeader` — главное
 * действие уходит в верхнюю панель на широком экране и в общую нижнюю
 * полосу на телефоне (раньше — своя липкая панель `PickProgressBar`), а
 * причина «почему недоступно» — подписью над ней.
 */

import { AlertTriangle, CheckCircle2, PlayCircle } from "lucide-react";
import type { ReactNode } from "react";

import { usePickingTaskMenu } from "./usePickingTaskMenu";
import { Button } from "../../ui/button";
import type { PageAction } from "../../ui/page-actions";
import { useScanSoundAction } from "../useScanSoundAction";
import type { PickingTaskDetailDto } from "../../../lib/api-picking";

export interface PickingTaskActionProps {
  task: PickingTaskDetailDto;
  currentUserId: string | null;
  /** Право SHOP_WMS_MANAGE — освободить, вернуть в очередь, пересобрать, отменить. */
  canManage: boolean;
  /** Задание ничьё и текущий пользователь может его взять. */
  canClaim: boolean;
  /** Собрано всё и задание можно проводить. */
  canComplete: boolean;
  /** Есть отмеченные недостачи — доступен исход «недостача». */
  canShortage: boolean;
  /**
   * Строк, закрытых недостачей. >0 — главным действием становится «Оформить
   * недостачу»: собирать больше нечего, и прятать исход в «⋯» значит
   * оставить кладовщика с серой кнопкой без выхода (§3.3).
   */
  shortageCount: number;
  /** Подпись «почему проведение недоступно»; null — кнопка активна. */
  completeHint: string | null;
  /** Подпись «почему исход недостачи недоступен»; null — кнопка активна. */
  shortageHint: string | null;
  busy: boolean;
  onClaim: () => void;
  onComplete: () => void;
  onShortage: () => void;
  onRelease: () => void;
  onCancel: () => void;
  onRebuild: () => void;
  /** Печать листа комплектации; undefined — пункта нет. */
  onPrint?: () => void;
  printing?: boolean;
}

/** Что задание отдаёт шапке документа. */
export interface PickingTaskHeaderParts {
  action: ReactNode;
  menu: PageAction[];
  /** Причина под главной кнопкой; null — пусто. */
  hint: string | null;
  /** Подтверждения пунктов «⋯». */
  dialogs: ReactNode;
}

export function usePickingTaskHeaderActions(props: PickingTaskActionProps): PickingTaskHeaderParts {
  const { canClaim, canComplete, canShortage, shortageCount, completeHint, shortageHint, busy } = props;
  const scanSound = useScanSoundAction();
  const menu = usePickingTaskMenu({
    task: props.task,
    currentUserId: props.currentUserId,
    canManage: props.canManage,
    busy,
    onRelease: props.onRelease,
    onCancel: props.onCancel,
    onRebuild: props.onRebuild,
    onPrint: props.onPrint,
    printing: props.printing,
  });

  const shortageFirst = !canClaim && shortageCount > 0;
  // Причина под кнопкой: при недостаче объясняем, почему «Провести» больше не
  // главная; в остальных случаях — почему проведение недоступно.
  const hint = canClaim ? null : shortageFirst ? (canShortage ? completeHint : shortageHint) : canComplete ? null : completeHint;

  const action = canClaim ? (
    <Button variant="brand" onClick={props.onClaim} disabled={busy} loading={busy}>
      <PlayCircle className="size-4" />
      Взять в работу
    </Button>
  ) : shortageFirst ? (
    <Button variant="destructive" onClick={props.onShortage} disabled={!canShortage || busy} loading={busy}>
      <AlertTriangle className="size-4" />
      Оформить недостачу
    </Button>
  ) : (
    <Button variant="brand" onClick={props.onComplete} disabled={!canComplete || busy} loading={busy}>
      <CheckCircle2 className="size-4" />
      Провести сборку
    </Button>
  );

  // Пока идёт запрос, меню недоступно целиком (как раньше `menuDisabled`).
  const items: PageAction[] = [scanSound, ...menu.actions].map((a) => (busy ? { ...a, disabled: true } : a));
  return { action, menu: items, hint, dialogs: menu.dialog };
}
